import React, { useRef, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import useWindowSize from '@hooks/use-window-size';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';
import useStickyNavQuery from '@hooks/sticky-nav/use-sticky-nav-query';
import useStickyNavNavigation from '@hooks/sticky-nav/use-sticky-nav-navigation';
import useRouterUrlSync from '@hooks/sticky-nav/use-router-url-sync';
import useScrollToActiveItem from '@hooks/sticky-nav/use-scroll-to-active-item';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import {
  getCurrentUniverse,
  getActiveEntryPoint,
  shouldHideStickyNav,
  getStickyEntryPoints,
  getEffectiveAsPath,
  getSessionUniverse,
} from '@helpers/utils/sticky-nav/sticky-nav-utils';
import { isHomePage } from '@helpers/routing';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import styles from './sticky-entrypoint-nav.module.scss';

interface StickyEntrypointNavProps {
  className?: string;
}

const StickyEntrypointNav: React.FC<StickyEntrypointNavProps> = ({ className }) => {
  const router = useRouter();
  const { t: tA11y } = useTranslation('a11y');
  const { preferences } = useShoppingPreferences();
  const { isWiderThanMd, hasWindowSizeBeenSet } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const activeItemRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const hasTrackedViewRef = useRef<boolean>(false);

  const { currentUrl, setCurrentUrl } = useRouterUrlSync();

  const currentUniverse = useMemo(() => {
    // Priority: 1. URL query param, 2. sessionStorage (session preference), 3. persistedPreferences (localStorage/backend)
    // Match homepage behavior: use sessionStorage preference, not the backend-overwritten preferences state
    const universeFromQuery = router.query?.universe ? parseInt(router.query.universe as string, 10) : undefined;

    // Use session universe (ensures consistency with shopping preferences logic HP)
    const sessionUniverse = getSessionUniverse();

    return getCurrentUniverse({
      universeFromQuery,
      sessionUniverse,
      persistedUniverse: preferences?.universe,
    });
  }, [router.query?.universe, preferences?.universe]);

  const { stickyNavData } = useStickyNavQuery({ universe: currentUniverse });

  const entryPoints = useMemo(() => getStickyEntryPoints(stickyNavData), [stickyNavData]);

  const effectiveAsPath = useMemo(
    () => getEffectiveAsPath(currentUrl, router.asPath || ''),
    [currentUrl, router.asPath],
  );

  const activeEntryPoint = useMemo(
    () =>
      getActiveEntryPoint({
        routerPathname: router.pathname,
        routerAsPath: effectiveAsPath,
        entryPoints,
        currentUniverse,
      }),
    [router.pathname, effectiveAsPath, entryPoints, currentUniverse],
  );

  const { handleEntryPointClick, buildLinkHref } = useStickyNavNavigation({
    setCurrentUrl,
    currentUniverse,
  });

  const { sendAnalyticEvent } = useAnalyticEvents('navigation');

  useScrollToActiveItem({
    activeItemId: activeEntryPoint,
    containerRef,
    itemRefs: activeItemRefs,
    items: entryPoints,
  });

  const shouldHide = useMemo(
    () =>
      shouldHideStickyNav({
        isWiderThanMd,
        hasWindowSizeBeenSet,
      }),
    [isWiderThanMd, hasWindowSizeBeenSet],
  );

  useEffect(() => {
    if (hasTrackedViewRef.current) {
      return;
    }

    if (shouldHide || !entryPoints || entryPoints.length === 0) {
      return;
    }

    sendAnalyticEvent('view_sticky_navigation', {
      label: 'sticky_navigation',
    });
    hasTrackedViewRef.current = true;
  }, [entryPoints?.length, shouldHide]);

  if (shouldHide) {
    return null;
  }

  if (!entryPoints || entryPoints.length === 0) {
    return null;
  }

  return (
    <nav
      className={clsx(styles.stickyNav, className)}
      role="navigation"
      aria-label={tA11y('A11Y.STICKY_NAV.LABEL')}
    >
      <div
        ref={containerRef}
        className={styles.stickyNav__container}
      >
        <ul className={styles.stickyNav__list}>
          {entryPoints.map((entryPoint) => {
            const isActive = activeEntryPoint === entryPoint.id;
            const isFromStickyNav = router.query?.fromStickyNav === '1';
            // Use h1 for active item when fromStickyNav=1 on PLP (not HP)
            const shouldUseH1 = isActive && isFromStickyNav && !isHomePage(router.pathname, router.asPath);

            return (
              <li
                key={entryPoint.id}
                ref={(el) => {
                  if (el) {
                    activeItemRefs.current.set(entryPoint.id, el);
                  } else {
                    activeItemRefs.current.delete(entryPoint.id);
                  }
                }}
                className={styles.stickyNav__item}
              >
                <ConditionalWrapper
                  condition={shouldUseH1}
                  wrap={(children) => <h1 className={styles.stickyNav__heading}>{children}</h1>}
                >
                  <a
                    href={buildLinkHref(entryPoint)}
                    className={clsx(styles.stickyNav__button, isActive && styles['stickyNav__button--active'])}
                    onClick={(e) => handleEntryPointClick(e, entryPoint)}
                    aria-current={isActive ? 'page' : undefined}
                    data-cy={`sticky-nav-${entryPoint.mnemonic}`}
                    data-vc-dd-action-name={`sticky_nav_${entryPoint.mnemonic}`}
                  >
                    {entryPoint.title}
                  </a>
                </ConditionalWrapper>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default StickyEntrypointNav;
